import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { Card, CardContent, Grid, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { IconCircleCheck, IconAlertTriangle } from '@tabler/icons';

interface IntegrationSummaryCardProps {
    title: string;
    successCount: number;
    errorCount: number;
    totalCount: number;
    successRate: number;
    icon: React.ComponentType<any>;
    color: string;
}

const IntegrationSummaryCard = ({
    title,
    successCount,
    errorCount,
    totalCount,
    successRate,
    icon: Icon,
    color
}: IntegrationSummaryCardProps) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Color logic based on success rate
    let rateColor = theme.palette.error.main;
    let rateBg = isDark ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.error.light, 0.4);
    if (successRate >= 95) {
        rateColor = theme.palette.success.main;
        rateBg = isDark ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.success.light, 0.4);
    } else if (successRate >= 80) {
        rateColor = theme.palette.warning.main;
        rateBg = isDark ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.warning.light, 0.4);
    }

    return (
        <Card
            sx={{
                background: isDark
                    ? `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.15)} 100%)`
                    : `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.1)} 100%)`,
                border: `1px solid ${alpha(color, 0.2)}`,
                borderRadius: '16px',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isDark ? `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.3)}` : `0 8px 32px 0 ${alpha(color, 0.08)}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark ? `0 12px 40px 0 ${alpha(theme.palette.common.black, 0.5)}` : `0 12px 40px 0 ${alpha(color, 0.15)}`,
                    borderColor: alpha(color, 0.4)
                }
            }}
        >
            {/* Absolute background icon */}
            <Box
                sx={{
                    position: 'absolute',
                    right: -10,
                    top: -10,
                    color,
                    opacity: isDark ? 0.06 : 0.08,
                    '& svg': {
                        width: '120px',
                        height: '120px'
                    },
                    pointerEvents: 'none'
                }}
            >
                <Icon />
            </Box>

            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                <Grid container spacing={2}>
                    {/* Header: Title and Icon */}
                    <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                                sx={{
                                    p: 1,
                                    borderRadius: '12px',
                                    bgcolor: alpha(color, 0.1),
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Icon size="1.5rem" stroke={1.5} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {title}
                            </Typography>
                        </Box>
                        <Chip
                            label={`${successRate.toFixed(1)}% Success`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                color: rateColor,
                                bgcolor: rateBg,
                                border: `1px solid ${alpha(rateColor, 0.3)}`
                            }}
                        />
                    </Grid>

                    {/* Main metric (Total) */}
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            TOTAL TRANSACTIONS (24H)
                        </Typography>
                        <Typography variant="h2" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            {totalCount.toLocaleString()}
                        </Typography>
                    </Grid>

                    {/* Progress Bar */}
                    <Grid item xs={12} sx={{ my: 0.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={successRate}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: isDark ? alpha(theme.palette.divider, 0.1) : theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    bgcolor: rateColor
                                }
                            }}
                        />
                    </Grid>

                    {/* Detail Metrics: Success & Error */}
                    <Grid item xs={12} display="flex" gap={2}>
                        <Box
                            sx={{
                                flex: 1,
                                p: 1.5,
                                borderRadius: '12px',
                                bgcolor: isDark ? alpha(theme.palette.common.black, 0.2) : theme.palette.common.white,
                                border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.05) : theme.palette.grey[200]}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <IconCircleCheck size="1.2rem" stroke={1.5} style={{ color: theme.palette.success.main }} />
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>
                                    Success
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                                    {successCount.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                p: 1.5,
                                borderRadius: '12px',
                                bgcolor: isDark ? alpha(theme.palette.common.black, 0.2) : theme.palette.common.white,
                                border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.05) : theme.palette.grey[200]}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <IconAlertTriangle
                                size="1.2rem"
                                stroke={1.5}
                                style={{ color: errorCount > 0 ? theme.palette.error.main : theme.palette.text.secondary }}
                            />
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>
                                    Errors
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, mt: 0.5, color: errorCount > 0 ? theme.palette.error.main : 'inherit' }}
                                >
                                    {errorCount.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default IntegrationSummaryCard;
